'use client';

import { useAnalytics } from '@/hooks/useAnalytics';

// WhatsApp icon as SVG component
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

interface DualCTAProps {
  bookCallText: string;
  whatsappText: string;
  whatsappLink: string;
  variant?: 'hero' | 'section' | 'sticky';
  isRtl?: boolean;
}

export default function DualCTA({
  bookCallText,
  whatsappText,
  whatsappLink,
  variant = 'hero',
  isRtl = false,
}: DualCTAProps) {
  const { trackEvent, trackWhatsAppClick } = useAnalytics();
  const baseStyles = {
    hero: 'flex-row gap-3 sm:gap-4 flex-wrap',
    section: 'flex-row gap-2.5 sm:gap-3 flex-wrap',
    sticky: 'flex-row gap-2',
  };

  const buttonSizeStyles = {
    hero: 'h-12 sm:h-14 px-4 sm:px-8 text-sm sm:text-lg',
    section: 'h-11 sm:h-12 px-4 sm:px-6 text-sm sm:text-base',
    sticky: 'h-10 px-4 text-sm',
  };

  return (
    <div className={`flex items-center justify-center ${baseStyles[variant]}`}>
      {/* Primary CTA - Book a Call */}
      <a
        href="#form"
        onClick={() =>
          trackEvent('cta_clicked', {
            label: 'book_call',
            page: 'persona_lp',
            location: `dual_cta_${variant}`,
          })
        }
        className={`
          group relative overflow-hidden inline-flex items-center justify-center
          bg-gradient-to-r from-purple-dark via-purple to-purple-dark
          hover:from-purple hover:via-purple-light hover:to-purple
          text-white font-bold rounded-xl
          border border-purple-light/50
          shadow-2xl shadow-purple/30 hover:shadow-purple-light/40
          hover:scale-[1.02] transition-all duration-300
          ${buttonSizeStyles[variant]}
        `}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
        <span className="relative z-10 flex items-center gap-2">
          {bookCallText}
          <svg
            className={`w-5 h-5 transition-transform ${isRtl ? 'rotate-180 group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </span>
      </a>

      {/* Secondary CTA - WhatsApp */}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          trackEvent('cta_clicked', {
            label: 'whatsapp',
            page: 'persona_lp',
            location: `dual_cta_${variant}`,
          });
          trackWhatsAppClick({
            page: 'persona_lp',
            location: `dual_cta_${variant}`,
          });
        }}
        className={`
          group inline-flex items-center justify-center gap-2
          bg-[#25D366] hover:bg-[#20BD5A]
          text-white font-bold rounded-xl
          border border-green-400/50
          shadow-lg shadow-green-500/20 hover:shadow-green-400/30
          hover:scale-[1.02] transition-all duration-300
          ${buttonSizeStyles[variant]}
        `}
      >
        <WhatsAppIcon className="w-5 h-5" />
        <span>{whatsappText}</span>
      </a>
    </div>
  );
}
